import type {
	MailAttachment,
	MailBulk,
	MailCompose,
	MailFolder,
	MailMessage,
	MailMessageSummary,
	MailStatus
} from '@andresot/protocol/mail';
import * as api from '$api/mail';
import { SvelteSet } from 'svelte/reactivity';

/**
 * Сколько ждать после последней буквы, прежде чем сохранить черновик.
 *
 * Не по каждому нажатию: каждое сохранение — это создание новой версии письма
 * на почтовом сервере и удаление прежней, и печатать под такой стрекот нельзя.
 * Три секунды — это пауза, которую человек делает, дописав мысль.
 */
const AUTOSAVE_MS = 3_000;

/**
 * Сколько секунд можно передумать после «Отправить».
 *
 * Десять — столько, чтобы успеть заметить забытое вложение и не тот адрес, и
 * при этом не сидеть над экраном в ожидании, когда же письмо наконец уйдёт.
 */
const UNDO_SEND_SECONDS = 10;

export interface MailDraft {
	id?: string;
	to: string;
	cc: string;
	bcc: string;
	subject: string;
	bodyText: string;
	attachments: MailAttachment[];
	replyToId?: string;
	forwardOfId?: string;
}

function emptyDraft(): MailDraft {
	return { to: '', cc: '', bcc: '', subject: '', bodyText: '', attachments: [] };
}

/**
 * Начать письмо с подписи.
 *
 * Подпись ставится в текст, а не приклеивается сервером при отправке: человек
 * должен видеть письмо целиком до того, как нажмёт «Отправить», и уметь
 * поправить подпись в конкретном письме, ничего не меняя в настройках.
 *
 * Разделитель «-- » — не украшение: по нему почтовые программы отличают
 * подпись от текста и прячут её в цитате при ответе.
 */
function withSignature(body: string, signature?: string): string {
	const value = signature?.trim();
	return value ? `${body}\n\n-- \n${value}` : body;
}

function recipients(value: string): string[] {
	return value
		.split(/[;,\n]/)
		.map((entry) => entry.trim())
		.filter(Boolean);
}

function recipientLine(value: MailMessage['to']): string {
	return value.map((entry) => entry.email).join(', ');
}

function editableDraft(message: MailMessage): MailDraft {
	return {
		id: message.id,
		to: recipientLine(message.to),
		cc: recipientLine(message.cc),
		bcc: recipientLine(message.bcc),
		subject: message.subject,
		bodyText: message.bodyText,
		attachments: [...message.attachments]
	};
}

class MailState {
	status = $state<MailStatus>({ configured: false, available: false, provisioned: false });
	folders = $state<MailFolder[]>([]);
	messages = $state<MailMessageSummary[]>([]);
	selectedFolderId = $state<string | null>(null);
	selectedId = $state<string | null>(null);
	selected = $state<MailMessage | null>(null);
	/**
	 * Вся переписка открытого письма, от старых к новым.
	 *
	 * Одно письмо — это тот же список из одного, чтобы у показа была одна форма,
	 * а не две расходящиеся ветки.
	 */
	thread = $state<MailMessage[]>([]);
	search = $state('');
	loading = $state(false);
	loadingMessage = $state(false);
	total = $state(0);
	hasMore = $state(false);
	draft = $state<MailDraft | null>(null);
	operation = $state<'idle' | 'saving' | 'sending'>('idle');
	/** Выделенные в списке письма — для действий над пачкой сразу. */
	selection = $state<string[]>([]);
	/**
	 * Что происходит с черновиком само по себе.
	 *
	 * Показывается в редакторе намеренно: молчаливое автосохранение, о котором
	 * никак не сказано, человек попросту не считает сохранением — и всё равно
	 * жмёт «Сохранить» перед закрытием, на всякий случай.
	 */
	autosave = $state<'idle' | 'saving' | 'saved' | 'failed'>('idle');
	/** Идёт отсчёт до отправки: сколько секунд осталось передумать. */
	pendingSend = $state<{ seconds: number } | null>(null);
	/** Чем кончилась отложенная отправка — показывается один раз и стирается. */
	sendResult = $state<{ ok: boolean; error?: string } | null>(null);
	private lifecycle = 0;
	/** Номер последнего запроса списка: по нему отбрасываются опоздавшие ответы. */
	private listRequest = 0;
	private messageRequest = 0;
	private navigationRequest = 0;
	private folderRequest = 0;
	private operationRequest = 0;
	private mutationRequest = 0;
	private held: MailCompose | null = null;
	private sendTimer: ReturnType<typeof setInterval> | null = null;
	private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
	/** Идущее автосохранение: его дожидаются отправка и ручное сохранение. */
	private autosaveRun: Promise<void> | null = null;
	/** Слепок последнего сохранённого черновика — чтобы не писать одно и то же. */
	private savedShape = '';

	available = $derived(this.status.available);
	signature = $derived(this.status.signature ?? '');
	selectionMode = $derived(this.selection.length > 0);
	saving = $derived(this.operation === 'saving');
	sending = $derived(this.operation === 'sending');
	busy = $derived(this.operation !== 'idle');
	inboxUnread = $derived(this.folders.find((folder) => folder.role === 'inbox')?.unread ?? 0);
	selectedFolder = $derived(
		this.folders.find((folder) => folder.id === this.selectedFolderId) ?? null
	);

	/**
	 * Начать работу с почтой.
	 *
	 * Адрес ящика приходит вместе с учётной записью, ещё до первого кадра.
	 * Поэтому признак «почта есть» ставится сразу, а не после ответа сервера:
	 * иначе пункт «Почта» в нижнем меню появлялся последним, уже после того как
	 * остальные разделы нарисовались, и меню перестраивалось на глазах.
	 *
	 * Настоящий ответ приходит следом и уточняет остальное: папки,
	 * непрочитанное, поломку заведения.
	 */
	async start(mailbox?: string): Promise<void> {
		this.stop();
		const lifecycle = ++this.lifecycle;
		this.loading = true;
		if (mailbox) this.status = { configured: true, available: true, provisioned: true, mailbox };
		try {
			const status = await api.fetchMailStatus();
			if (lifecycle !== this.lifecycle) return;
			this.status = status;
			if (lifecycle !== this.lifecycle || !this.status.available) return;
			const folders = await api.fetchMailFolders();
			if (lifecycle !== this.lifecycle) return;
			this.folders = folders;
			this.selectedFolderId =
				this.folders.find((folder) => folder.role === 'inbox')?.id ?? this.folders[0]?.id ?? null;
			this.loading = false;
			await this.loadMessages();
		} catch (error) {
			if (lifecycle === this.lifecycle) {
				this.status = {
					...this.status,
					available: false,
					error: error instanceof Error ? error.message : 'Почта недоступна'
				};
			}
		} finally {
			if (lifecycle === this.lifecycle) this.loading = false;
		}
	}

	stop(): void {
		this.lifecycle += 1;
		this.listRequest += 1;
		this.messageRequest += 1;
		this.navigationRequest += 1;
		this.folderRequest += 1;
		this.operationRequest += 1;
		this.cancelAutosave();
		// До отсчёта письмо уже сохранено на сервере. При выходе оставляем его
		// черновиком; отложенная операция не должна уйти от другого аккаунта.
		this.clearSendTimer();
		this.held = null;
		this.autosaveRun = null;
		this.savedShape = '';
		this.autosave = 'idle';
		this.sendResult = null;
		this.thread = [];
		this.status = { configured: false, available: false, provisioned: false };
		this.folders = [];
		this.messages = [];
		this.selectedFolderId = null;
		this.selectedId = null;
		this.selected = null;
		this.draft = null;
		this.selection = [];
		this.search = '';
		this.total = 0;
		this.hasMore = false;
		this.loading = false;
		this.loadingMessage = false;
		this.operation = 'idle';
	}

	/**
	 * Отпустить черновик, ничего не потеряв.
	 *
	 * Крестик редактора черновик сохранял, а вот переход в другую папку или
	 * открытие письма просто обнуляли его — вместе с уже загруженными
	 * вложениями, за которые человек заплатил своим временем и трафиком.
	 *
	 * Если сохранить не удалось, черновик остаётся на месте, а отказ уходит
	 * наверх: пусть лучше не откроется папка, чем пропадёт письмо.
	 *
	 * Возвращает `true`, если черновик и правда сохранён.
	 */
	async stashDraft(): Promise<boolean> {
		const lifecycle = this.lifecycle;
		const draft = this.draft;
		if (!draft) return false;
		this.cancelAutosave();
		await this.settleAutosave();
		if (lifecycle !== this.lifecycle || this.draft !== draft) return false;

		const hasContent = this.draftHasContent(draft);
		if (hasContent) await this.saveDraft();
		if (lifecycle !== this.lifecycle || this.draft !== draft) return false;
		this.draft = null;
		return hasContent;
	}

	private draftHasContent(draft: MailDraft): boolean {
		return Boolean(
			draft.id ||
			draft.to.trim() ||
			draft.cc.trim() ||
			draft.bcc.trim() ||
			draft.subject.trim() ||
			draft.bodyText.trim() ||
			draft.attachments.length
		);
	}

	/** Слепок черновика: по нему видно, есть ли что сохранять. */
	private shapeOf(draft: MailDraft): string {
		return JSON.stringify([
			draft.to,
			draft.cc,
			draft.bcc,
			draft.subject,
			draft.bodyText,
			draft.attachments.map((item) => item.blobId)
		]);
	}

	cancelAutosave(): void {
		if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
		this.autosaveTimer = null;
	}

	/**
	 * Отложить сохранение черновика.
	 *
	 * Вызывается на каждое изменение полей: таймер сдвигается, и запись уходит
	 * только тогда, когда человек перестал печатать.
	 */
	scheduleAutosave(): void {
		this.cancelAutosave();
		this.autosaveTimer = setTimeout(() => void this.runAutosave(), AUTOSAVE_MS);
	}

	/** Дождаться уже идущего автосохранения, чем бы оно ни кончилось. */
	private async settleAutosave(): Promise<void> {
		await this.autosaveRun?.catch(() => undefined);
	}

	/**
	 * Сохранение без нажатия.
	 *
	 * Без всплывающих сообщений: человек пишет письмо, и «черновик сохранён»
	 * каждые несколько секунд отвлекало бы от того, ради чего он здесь.
	 * Происходящее видно в самом редакторе строкой состояния — этого хватает,
	 * чтобы не жать «Сохранить» на всякий случай.
	 *
	 * Отказ тоже не выносим наверх: черновик остаётся в редакторе, попытка
	 * повторится с ближайшим изменением, а закрытие редактора сохранит его уже
	 * с обычным ответом об ошибке.
	 */
	private async runAutosave(): Promise<void> {
		const lifecycle = this.lifecycle;
		const draft = this.draft;
		if (!draft || this.busy || !this.draftHasContent(draft)) return;
		const shape = this.shapeOf(draft);
		if (shape === this.savedShape) return;

		this.autosave = 'saving';
		const run = (async () => {
			try {
				const result = await api.saveMailDraft(this.payload());
				// Черновик мог смениться, пока шла запись: тогда идентификатор от
				// прежнего письма приписывать новому нельзя.
				if (lifecycle !== this.lifecycle) return;
				if (this.draft === draft) {
					draft.id = result.message.id;
					this.savedShape = shape;
					this.autosave = 'saved';
				}
				await this.refreshFoldersAndList(lifecycle);
			} catch {
				if (lifecycle === this.lifecycle && this.draft === draft) this.autosave = 'failed';
			}
		})();
		this.autosaveRun = run;
		try {
			await run;
		} finally {
			if (this.autosaveRun === run) this.autosaveRun = null;
		}
	}

	async openFolder(id: string): Promise<void> {
		const lifecycle = this.lifecycle;
		const navigation = ++this.navigationRequest;
		this.messageRequest += 1;
		await this.stashDraft();
		if (lifecycle !== this.lifecycle || navigation !== this.navigationRequest) return;
		this.selectedFolderId = id;
		this.selectedId = null;
		this.selected = null;
		this.thread = [];
		this.loadingMessage = false;
		this.selection = [];
		await this.loadMessages();
	}

	async setSearch(value: string): Promise<void> {
		this.navigationRequest += 1;
		this.messageRequest += 1;
		this.search = value;
		this.selectedId = null;
		this.selected = null;
		this.thread = [];
		this.loadingMessage = false;
		this.selection = [];
		await this.loadMessages();
	}

	/**
	 * Загрузить список писем.
	 *
	 * Новый запрос списка главнее идущего. Раньше второй просто не начинался,
	 * пока не кончится первый: человек успевал перейти в «Отправленные» или
	 * дописать слово в поиске, а на экране оставались письма прежней папки и
	 * прежнего запроса — под новым заголовком. Теперь отвечает последний
	 * спросивший, а опоздавший ответ отбрасывается.
	 */
	async loadMessages(append = false): Promise<void> {
		const lifecycle = this.lifecycle;
		if (!this.available) return;
		// Догрузка следующей страницы дважды не нужна — ни поверх себя, ни поверх нового списка.
		if (append && this.loading) return;
		const request = ++this.listRequest;
		this.loading = true;
		try {
			const result = await api.fetchMailMessages({
				mailboxId: this.selectedFolderId ?? undefined,
				text: this.search.trim() || undefined,
				position: append ? this.messages.length : 0,
				limit: 50
			});
			if (lifecycle !== this.lifecycle || request !== this.listRequest) return;
			this.messages = append ? [...this.messages, ...result.messages] : result.messages;
			this.total = result.total;
			this.hasMore = result.hasMore;
		} catch (error) {
			if (lifecycle === this.lifecycle && request === this.listRequest) throw error;
		} finally {
			if (lifecycle === this.lifecycle && request === this.listRequest) this.loading = false;
		}
	}

	async selectMessage(id: string): Promise<void> {
		const lifecycle = this.lifecycle;
		const navigation = ++this.navigationRequest;
		const request = ++this.messageRequest;
		const current = () => lifecycle === this.lifecycle && request === this.messageRequest;
		await this.stashDraft();
		if (!current() || navigation !== this.navigationRequest) return;
		this.selectedId = id;
		this.selected = null;
		this.loadingMessage = true;
		this.thread = [];
		try {
			const opened = await api.fetchMailMessage(id);
			// Пока письмо ехало, открыли другое: медленный ответ не должен лечь поверх него.
			if (!current()) return;
			if (opened.isDraft) {
				this.selectedId = null;
				this.selected = null;
				this.startDraft(editableDraft(opened), true);
			} else {
				this.selected = opened;
				/*
				 * Разговор читается целиком, а не по письму с возвратом к списку.
				 * Загружаем его следом за письмом, а не вместо: открытое письмо уже
				 * на экране, пока едет остальная переписка.
				 */
				const summary = this.messages.find((item) => item.id === id);
				this.thread = [opened];
				if ((summary?.threadCount ?? 1) > 1) {
					const letters = await api.fetchMailThread(opened.threadId).catch(() => null);
					if (!current()) return;
					if (letters?.length) this.thread = letters;
				}
			}
			const summary = this.messages.find((item) => item.id === id);
			if (!opened.isDraft && summary?.isUnread) {
				summary.isUnread = false;
				const folder = this.folders.find((item) => item.id === this.selectedFolderId);
				if (folder) folder.unread = Math.max(0, folder.unread - 1);
			}
		} catch (error) {
			if (current()) throw error;
		} finally {
			// Опоздавший ответ не гасит «загружаем» у письма, которое открывают сейчас.
			if (current()) this.loadingMessage = false;
		}
	}

	compose(): void {
		this.navigationRequest += 1;
		this.selectedId = null;
		this.selected = null;
		this.startDraft({ ...emptyDraft(), bodyText: withSignature('', this.signature) });
	}

	/**
	 * Открыть редактор с готовым черновиком.
	 *
	 * `alreadySaved` — про письмо, поднятое из «Черновиков»: оно на сервере уже
	 * лежит ровно в таком виде, и автосохранение не должно переписывать его
	 * только оттого, что редактор открыли.
	 */
	private startDraft(draft: MailDraft, alreadySaved = false): void {
		this.messageRequest += 1;
		this.loadingMessage = false;
		this.cancelAutosave();
		this.savedShape = alreadySaved ? this.shapeOf(draft) : '';
		this.autosave = alreadySaved ? 'saved' : 'idle';
		this.draft = draft;
	}

	private replySubject(): string {
		const subject = this.selected?.subject ?? '';
		return subject.toLowerCase().startsWith('re:') ? subject : `Re: ${subject}`;
	}

	reply(): void {
		if (!this.selected) return;
		const senders = this.selected.replyTo?.length ? this.selected.replyTo : this.selected.from;
		this.startDraft({
			...emptyDraft(),
			to: this.replyRecipients(senders).join(', '),
			subject: this.replySubject(),
			bodyText: withSignature('', this.signature),
			replyToId: this.selected.id
		});
	}

	/**
	 * Ответить всем участникам переписки.
	 *
	 * Свой адрес из получателей вычёркивается: письмо самому себе — это лишняя
	 * копия во «Входящих» и ложное непрочитанное. Скрытую копию не
	 * восстанавливаем: она была скрытой не случайно.
	 */
	replyAll(): void {
		if (!this.selected) return;
		const senders = this.selected.replyTo?.length ? this.selected.replyTo : this.selected.from;
		const to = this.replyRecipients([...senders, ...this.selected.to]);
		const cc = this.replyRecipients(this.selected.cc, to);

		this.startDraft({
			...emptyDraft(),
			to: to.join(', '),
			cc: cc.join(', '),
			subject: this.replySubject(),
			bodyText: withSignature('', this.signature),
			replyToId: this.selected.id
		});
	}

	private replyRecipients(addresses: MailMessage['to'], excluded: string[] = []): string[] {
		const seen = new SvelteSet(
			[this.status.mailbox ?? '', ...excluded].map((email) => email.toLowerCase())
		);
		return addresses.flatMap(({ email }) => {
			const key = email.toLowerCase();
			if (seen.has(key)) return [];
			seen.add(key);
			return [email];
		});
	}

	forward(): void {
		if (!this.selected) return;
		this.startDraft({
			...emptyDraft(),
			subject: this.selected.subject.toLowerCase().startsWith('fwd:')
				? this.selected.subject
				: `Fwd: ${this.selected.subject}`,
			bodyText: withSignature('', this.signature),
			forwardOfId: this.selected.id,
			attachments: [...this.selected.attachments]
		});
	}

	private payload(draft = this.draft): MailCompose {
		if (!draft) throw new Error('Черновик не открыт');
		return {
			...(draft.id ? { id: draft.id } : {}),
			to: recipients(draft.to),
			cc: recipients(draft.cc),
			bcc: recipients(draft.bcc),
			subject: draft.subject,
			bodyText: draft.bodyText,
			attachments: [...draft.attachments],
			...(draft.replyToId ? { replyToId: draft.replyToId } : {}),
			...(draft.forwardOfId ? { forwardOfId: draft.forwardOfId } : {})
		};
	}

	async saveDraft(): Promise<void> {
		const lifecycle = this.lifecycle;
		const draft = this.draft;
		if (!draft) throw new Error('Черновик не открыт');
		// Ручное сохранение перебивает отложенное, а идущее — дожидается: иначе
		// две записи одного письма разошлись бы и оставили лишнюю версию.
		this.cancelAutosave();
		await this.settleAutosave();
		if (lifecycle !== this.lifecycle || this.draft !== draft) return;
		if (this.busy) throw new Error('Дождитесь завершения почтовой операции');
		this.operation = 'saving';
		const operation = ++this.operationRequest;
		const shape = this.shapeOf(draft);
		try {
			const result = await api.saveMailDraft(this.payload(draft));
			if (lifecycle !== this.lifecycle) return;
			if (this.draft === draft) {
				draft.id = result.message.id;
				this.savedShape = shape;
				this.autosave = 'saved';
			}
			await this.refreshFoldersAndList(lifecycle);
		} catch (error) {
			if (lifecycle === this.lifecycle && this.draft === draft) throw error;
		} finally {
			if (lifecycle === this.lifecycle && operation === this.operationRequest)
				this.operation = 'idle';
		}
	}

	/**
	 * Отправить — но не сразу.
	 *
	 * Между «Отправить» и уходом письма есть несколько секунд, чтобы передумать:
	 * забытое вложение, не тот адресат, «во вложении» без вложения. Вернуть
	 * ушедшее письмо нельзя, а вот придержать его — можно.
	 *
	 * Придерживаем в приложении, а не на почтовом сервере: отложенной отправки
	 * он не объявляет — спрашивали. Поэтому письмо сначала сохраняется
	 * черновиком, и если приложение закроют посреди отсчёта, оно останется
	 * в «Черновиках», а не пропадёт.
	 */
	async send(): Promise<void> {
		const lifecycle = this.lifecycle;
		const draft = this.draft;
		if (!draft) throw new Error('Черновик не открыт');
		this.cancelAutosave();
		await this.settleAutosave();
		if (lifecycle !== this.lifecycle || this.draft !== draft) return;
		if (this.busy) throw new Error('Дождитесь завершения почтовой операции');

		this.operation = 'sending';
		const operation = ++this.operationRequest;
		const payload = this.payload(draft);
		try {
			// Сохраняем до отсчёта: закрытая вкладка тогда стоит человеку
			// черновика, а не письма.
			const saved = await api.saveMailDraft(payload);
			if (lifecycle !== this.lifecycle || this.draft !== draft) return;
			this.held = { ...payload, id: saved.message.id };
			this.draft = null;
			this.savedShape = '';
		} catch (error) {
			if (lifecycle === this.lifecycle && this.draft === draft) throw error;
			return;
		} finally {
			if (lifecycle === this.lifecycle && operation === this.operationRequest)
				this.operation = 'idle';
		}

		this.startSendCountdown();
		await this.refreshFoldersAndList(lifecycle);
	}

	private startSendCountdown(): void {
		this.clearSendTimer();
		this.pendingSend = { seconds: UNDO_SEND_SECONDS };
		this.sendTimer = setInterval(() => {
			if (!this.pendingSend) return;
			const left = this.pendingSend.seconds - 1;
			if (left > 0) {
				this.pendingSend = { seconds: left };
				return;
			}
			void this.flushSend();
		}, 1_000);
	}

	private clearSendTimer(): void {
		if (this.sendTimer) clearInterval(this.sendTimer);
		this.sendTimer = null;
		this.pendingSend = null;
	}

	/** Отсчёт кончился — письмо уходит по-настоящему. */
	private async flushSend(): Promise<void> {
		const lifecycle = this.lifecycle;
		const held = this.held;
		this.clearSendTimer();
		this.held = null;
		if (!held) return;
		try {
			await api.sendMail(held);
			if (lifecycle !== this.lifecycle) return;
			this.sendResult = { ok: true };
		} catch (error) {
			if (lifecycle !== this.lifecycle) return;
			this.sendResult = {
				ok: false,
				error: error instanceof Error ? error.message : 'Письмо не отправлено'
			};
		}
		// Отправка уже завершена. Сбой обновления списка не повторяет её.
		await this.refreshFoldersAndList(lifecycle).catch(() => undefined);
	}

	/**
	 * Передумал.
	 *
	 * Письмо возвращается в редактор ровно тем, чем было: черновик всё это время
	 * лежал на сервере, и терять при отмене нечего.
	 */
	undoSend(): void {
		const held = this.held;
		this.clearSendTimer();
		this.held = null;
		if (!held) return;
		this.startDraft(
			{
				...(held.id ? { id: held.id } : {}),
				to: held.to.join(', '),
				cc: held.cc.join(', '),
				bcc: held.bcc.join(', '),
				subject: held.subject,
				bodyText: held.bodyText,
				attachments: [...held.attachments],
				...(held.replyToId ? { replyToId: held.replyToId } : {}),
				...(held.forwardOfId ? { forwardOfId: held.forwardOfId } : {})
			},
			true
		);
	}

	/**
	 * Отправить придержанное немедленно.
	 *
	 * Явный запрос отправки внутри текущей сессии. Выход отменяет отсчёт
	 * и оставляет сохранённый черновик на сервере.
	 */
	async flushPendingSend(): Promise<void> {
		if (this.held) await this.flushSend();
	}

	async removeSelected(): Promise<void> {
		const lifecycle = this.lifecycle;
		const selected = this.selected;
		if (!selected) return;
		try {
			await api.deleteMailMessage(selected.id);
			if (lifecycle !== this.lifecycle) return;
			if (this.selected === selected) {
				this.messageRequest += 1;
				this.selected = null;
				this.selectedId = null;
				this.thread = [];
			}
			await this.refreshFoldersAndList(lifecycle);
		} catch (error) {
			if (lifecycle === this.lifecycle) throw error;
		}
	}

	async toggleFlag(): Promise<void> {
		const lifecycle = this.lifecycle;
		const selected = this.selected;
		if (!selected) return;
		const request = ++this.mutationRequest;
		const flagged = !selected.isFlagged;
		try {
			await api.updateMailMessage(selected.id, { flagged });
			if (lifecycle !== this.lifecycle || request !== this.mutationRequest) return;
			if (this.selected === selected) selected.isFlagged = flagged;
			const row = this.messages.find((item) => item.id === selected.id);
			if (row) row.isFlagged = flagged;
		} catch (error) {
			if (lifecycle === this.lifecycle && request === this.mutationRequest) throw error;
		}
	}

	/** «В спам» и «Не спам» для открытого письма. */
	async markJunk(junk: boolean): Promise<void> {
		const lifecycle = this.lifecycle;
		const selected = this.selected;
		if (!selected) return;
		try {
			await api.updateMailMessage(selected.id, { junk });
			if (lifecycle !== this.lifecycle) return;
			if (this.selected === selected) {
				this.messageRequest += 1;
				this.selected = null;
				this.selectedId = null;
				this.thread = [];
			}
			await this.refreshFoldersAndList(lifecycle);
		} catch (error) {
			if (lifecycle === this.lifecycle) throw error;
		}
	}

	/**
	 * Пришло письмо, а почта не открыта.
	 *
	 * Счётчик «Входящих» правим на месте: перечитывать папки ради одной цифры —
	 * лишний поход к почтовому серверу на каждое письмо. Список писем не
	 * трогаем: подменять содержимое экрана под руками у человека нельзя, а во
	 * «Входящих» он всё равно увидит новое при следующем открытии папки.
	 */
	applyIncoming(unread: number): void {
		const inbox = this.folders.find((folder) => folder.role === 'inbox');
		if (inbox) inbox.unread = unread;
	}

	toggleSelected(id: string): void {
		this.selection = this.selection.includes(id)
			? this.selection.filter((item) => item !== id)
			: [...this.selection, id];
	}

	clearSelection(): void {
		this.selection = [];
	}

	selectAllShown(): void {
		this.selection =
			this.selection.length === this.messages.length ? [] : this.messages.map((item) => item.id);
	}

	/** Одно действие над всеми выделенными письмами. */
	async bulk(change: Omit<MailBulk, 'ids'>): Promise<number> {
		const lifecycle = this.lifecycle;
		if (this.selection.length === 0) return 0;
		const selection = this.selection;
		let changed: number;
		try {
			({ changed } = await api.bulkMailMessages({ ...change, ids: [...selection] }));
			if (lifecycle !== this.lifecycle) return 0;
			if (this.selection === selection) this.selection = [];
			await this.refreshFoldersAndList(lifecycle);
		} catch (error) {
			if (lifecycle === this.lifecycle) throw error;
			return 0;
		}
		if (lifecycle !== this.lifecycle) return 0;
		/*
		 * Открытое письмо могло уехать в другую папку вместе с пачкой. Сверяемся
		 * со свежим списком: раньше проверка шла до перечитывания, когда уехавшее
		 * ещё числилось на месте, — и удалённое письмо оставалось открытым.
		 */
		if (this.selectedId && !this.messages.some((item) => item.id === this.selectedId)) {
			this.selectedId = null;
			this.selected = null;
			this.thread = [];
		}
		return changed;
	}

	private async refreshFoldersAndList(lifecycle = this.lifecycle): Promise<void> {
		if (lifecycle !== this.lifecycle) return;
		const request = ++this.folderRequest;
		const folders = await api.fetchMailFolders();
		if (lifecycle !== this.lifecycle || request !== this.folderRequest) return;
		this.folders = folders;
		await this.loadMessages();
	}
}

export const mail = new MailState();
